import { CMS_API_URL } from '../config/apiBase';
import { publicHref } from '../config/deployment';

/**
 * Online-meeting booking ("Book onlinemøde").
 *
 * Two halves:
 *  1. The lead (name / email / phone) is POSTed to the backend so it's captured
 *     even if the customer never finishes booking.
 *  2. The customer is then sent to the external StudentMeet calendar, which
 *     returns them to the brand landing carrying `?meeting=booked` so the page
 *     can confirm the booking.
 */

/**
 * StudentMeet calendar. Static for now — this becomes per-brand / per-rep later,
 * at which point it moves behind the brand settings API.
 */
export const MEETING_CALENDAR_URL = 'https://www.studentmeet.io/calendar/18d3-7a69-9ba3?cls=1';

/**
 * Query param StudentMeet reads to know where to send the customer afterwards.
 *
 * ⚠️ UNCONFIRMED — placeholder until StudentMeet confirms the parameter name.
 * When they do, this one constant is the only thing that changes.
 */
export const RETURN_PARAM = 'redirect_url';

/** Marker the brand landing looks for to show the "meeting booked" toast. */
export const BOOKED_PARAM = 'meeting';
export const BOOKED_VALUE = 'booked';

/**
 * Build the calendar URL, telling StudentMeet to return the customer to
 * `returnPath` with the booked marker attached.
 *
 * The return URL must be absolute and must use the PUBLIC path — on the public
 * domains the customer sees e.g. `clseifert.com/dk/merch`, not the internal
 * `/clothing/cls-dk` route (see config/deployment.js).
 *
 * @param {Object} opts
 * @param {string} opts.returnPath - internal path, e.g. '/clothing/cls-dk'
 * @returns {string} absolute calendar URL
 */
export function buildMeetingCalendarUrl({ returnPath }) {
  const url = new URL(MEETING_CALENDAR_URL);

  if (returnPath) {
    const returnUrl = new URL(publicHref(returnPath), window.location.origin);
    returnUrl.searchParams.set(BOOKED_PARAM, BOOKED_VALUE);
    url.searchParams.set(RETURN_PARAM, returnUrl.toString());
  }

  return url.toString();
}

/**
 * Submit a meeting request (lead) to the backend.
 *
 * Mirrors services/formsApi.js — same fetch shape and Laravel error unwrapping.
 *
 * NOTE: `POST /api/forms/{brand}/meeting` is not implemented on the backend yet.
 * Callers are expected to treat a rejection as non-fatal and continue to the
 * calendar, so the flow works before the endpoint ships.
 *
 * @param {string} apiBrandSlug - brand API slug (e.g. 'clsdk')
 * @param {Object} payload      - { name, email, phone }
 * @returns {Promise<Object>}   - { ok: true } on success
 * @throws  {Error}             - with a user-facing message on failure
 */
export async function submitMeetingRequest(apiBrandSlug, payload) {
  const res = await fetch(`${CMS_API_URL}/api/forms/${encodeURIComponent(apiBrandSlug)}/meeting`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    // Laravel validation → { message, errors: { field: [msg] } }; others → { error }.
    const validationMsg = body.errors ? Object.values(body.errors).flat()[0] : null;
    throw new Error(body.error || validationMsg || body.message || `Request failed (${res.status})`);
  }

  return res.json();
}

export default submitMeetingRequest;
